import type { ReactElement, ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import type { VenueFeatures } from '@lunit/shared-types';
import { useVenue } from '@core/contexts/VenueContext';
import { coreRuntime, type CoreCapabilities } from '@core/runtime';
import { HomePage } from '@core/pages/HomePage';
import { RewardsPage } from '@core/pages/RewardsPage';
import { HistoryPage } from '@core/pages/HistoryPage';
import { MenuPage } from '@core/pages/MenuPage';
import { PromotionsPage } from '@core/pages/PromotionsPage';
import { QRCodePage } from '@core/pages/QRCodePage';
import { InviteFriendPage } from '@core/pages/InviteFriendPage';
import { NotificationsPage } from '@core/pages/NotificationsPage';
import { GiftsPage } from '@core/pages/GiftsPage';
import { SendGiftPage } from '@core/pages/SendGiftPage';
import { BundlesPage } from '@core/pages/BundlesPage';
import { BundleQRPage } from '@core/pages/BundleQRPage';
import { PrivacyPolicyPage } from '@core/pages/PrivacyPolicyPage';
import { TermsOfServicePage } from '@core/pages/TermsOfServicePage';
import { MyDataPage } from '@core/pages/MyDataPage';
import GamePage from '@core/game/pages/GamePage';
import AchievementsPage from '@core/game/pages/AchievementsPage';
import { LeaderboardPage } from '@core/game/pages/LeaderboardPage';
import { EnergyShopPage } from '@core/game/pages/EnergyShopPage';

/**
 * Единая карта экранов клиента — одна на оба входа (мини-апп и PWA). Новый экран добавляется ТОЛЬКО сюда,
 * и тем же коммитом появляется в обоих входах: PWA не может отстать от мини-аппа по набору функций.
 *
 * access:
 *   - 'personal' — нужен вошедший клиент (данные из сессии). Мини-апп пускает всегда (клиент есть всегда),
 *     PWA оборачивает гвардом входа (нет сессии — экран входа по телефону);
 *   - 'public'   — открыт и без сессии (каталог, правовые документы).
 */
export interface CoreRouteDef {
  path: string;
  access: 'personal' | 'public';
  element: () => ReactElement;
}

export function coreRouteTable(
  features: Pick<VenueFeatures, 'giftsEnabled' | 'promotionsEnabled' | 'gameEnabled' | 'bundlesEnabled'> | null,
  capabilities: Pick<CoreCapabilities, 'game'>
): CoreRouteDef[] {
  const routes: CoreRouteDef[] = [
    { path: '/', access: 'personal', element: () => <HomePage /> },
    { path: '/rewards', access: 'public', element: () => <RewardsPage /> },
    { path: '/history', access: 'personal', element: () => <HistoryPage /> },
    { path: '/qr-code', access: 'personal', element: () => <QRCodePage /> },
    { path: '/invite-friend', access: 'personal', element: () => <InviteFriendPage /> },
    { path: '/notifications', access: 'personal', element: () => <NotificationsPage /> },
    // «Мои данные» и удаление данных (152-ФЗ, ст. 21) — в обоих входах.
    { path: '/my-data', access: 'personal', element: () => <MyDataPage /> },
    // Меню — всегда: экран сам говорит «Меню не опубликовано».
    { path: '/menu', access: 'public', element: () => <MenuPage /> },
  ];

  if (features?.giftsEnabled) {
    routes.push(
      { path: '/gifts', access: 'personal', element: () => <GiftsPage /> },
      { path: '/send-gift', access: 'personal', element: () => <SendGiftPage /> }
    );
  }
  if (features?.promotionsEnabled) {
    routes.push({ path: '/promotions', access: 'public', element: () => <PromotionsPage /> });
  }
  if (features?.gameEnabled && capabilities.game) {
    routes.push(
      { path: '/game', access: 'personal', element: () => <GamePage /> },
      { path: '/game/achievements', access: 'personal', element: () => <AchievementsPage /> },
      { path: '/game/leaderboard', access: 'personal', element: () => <LeaderboardPage /> },
      { path: '/game/shop', access: 'personal', element: () => <EnergyShopPage /> }
    );
  }
  if (features?.bundlesEnabled) {
    routes.push(
      { path: '/bundles', access: 'personal', element: () => <BundlesPage /> },
      { path: '/bundles/:id/qr', access: 'personal', element: () => <BundleQRPage /> }
    );
  }

  // Правовые документы — всегда и без сессии: ссылки на них уходят наружу.
  routes.push(
    { path: '/legal/privacy-policy', access: 'public', element: () => <PrivacyPolicyPage /> },
    { path: '/legal/terms', access: 'public', element: () => <TermsOfServicePage /> }
  );
  return routes;
}

interface CoreRoutesProps {
  /** Обёртка личных экранов (PWA — гвард сессии). По умолчанию — без обёртки (мини-апп). */
  personal?: (page: ReactElement) => ReactElement;
  /** Маршруты самого входа (PWA — /login). */
  children?: ReactNode;
}

const asIs = (page: ReactElement) => page;

/** Маршруты клиента для любого входа. Заведение и его фичи — из VenueProvider. */
export function CoreRoutes({ personal = asIs, children }: CoreRoutesProps) {
  const { features } = useVenue();
  const routes = coreRouteTable(features, coreRuntime().capabilities);

  return (
    <Routes>
      {children}
      {routes.map((r) => (
        <Route
          key={r.path}
          path={r.path}
          element={r.access === 'personal' ? personal(r.element()) : r.element()}
        />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
